import type { Call, INotification, TelnyxRTC } from '@telnyx/webrtc'
import { changerEtatTelephone, type EtatTelephone } from '@/stores/useEtatTelephoneStore'
import type { AppelAgent, EvenementAppel } from './types'

/**
 * The agent's Telnyx phone (@telnyx/webrtc), loaded only when the company
 * has a Telnyx number. One client per tab, like the Twilio Device.
 *
 * Outbound calls are "parked" by the Telnyx connection and handed to our
 * TeXML webhook, which checks the account and the caller ID exactly as for
 * Twilio: the browser only asks, the server decides.
 */

const TOKEN_EXPIRING_SOON = 34001
/** The SDK gave up reconnecting, or never reached the server: it already ended calls locally. */
const SDK_ABANDONNE = new Set([45001, 45003])
/** Login refused (LOGIN_FAILED, INVALID_CREDENTIALS, AUTHENTICATION_REQUIRED): expired or revoked token. */
const CONNEXION_REFUSEE = new Set([46001, 46002, 46003])

let client: TelnyxRTC | null = null
let pret = false
let jetonConnecte: string | null = null
let jetonDisponible: string | null = null
/**
 * Our server-signed agent identity, sent with every outbound call: TeXML does
 * not tell the webhook which SIP credential is calling. Only valid for this
 * agent, so it cannot be used to call on behalf of someone else.
 */
let identiteSignee: string | null = null
let renouvellementDemande = false
let demanderUnJeton: (() => void) | null = null
/** Logout or leaving the dashboard: no automatic reconnection after that. */
let arrete = false
/** Our own reconnections, only when the SDK cannot reconnect by itself. */
let tentatives = 0
let minuterieReconnexion: ReturnType<typeof setTimeout> | null = null
/** A fresh token was asked for; proposerJetonTelnyx() connects with it. */
let attenteJeton = false
let microChoisi: string | null = null
let hautParleurChoisi: string | null = null
const appels = new Map<string, AppelTelnyx>()
const surEntrants = new Set<(appel: AppelAgent) => void>()

const isDev = import.meta.env.DEV
const SONNERIE = 'https://sdk.twilio.com/js/client/sounds/releases/1.0.0/incoming.mp3'
// Played while the callee rings (North American ringback, 440 + 480 Hz, 2 s on
// 4 s off): Telnyx sends no ringback tone to the browser on its own.
const TONALITE_RETOUR = '/sons/tonalite-retour.wav'

class AppelTelnyx implements AppelAgent {
  readonly fournisseur = 'telnyx' as const
  private ecouteurs = new Map<EvenementAppel, Set<(erreur?: unknown) => void>>()
  private aEteActif = false
  private refuse = false
  private termine = false
  /** Replaced when the SDK brings the call back after a dropped connection. */
  call: Call
  private readonly numeroDistant: string

  constructor(call: Call, numeroDistant: string) {
    this.call = call
    this.numeroDistant = numeroDistant
  }

  get id(): string | null {
    return this.call.telnyxIDs?.telnyxCallControlId || null
  }

  get de(): string {
    return this.numeroDistant
  }

  accept() {
    void this.call.answer()
  }

  reject() {
    this.refuse = true
    void this.call.hangup()
  }

  disconnect() {
    void this.call.hangup()
  }

  mute(muet: boolean) {
    if (muet) this.call.muteAudio()
    else this.call.unmuteAudio()
  }

  private attente: { arreter: () => void; micro: MediaStreamTrack | null; piste: MediaStreamTrack } | null = null
  /** Hold music to send again once a recovered call is active. */
  private attenteAReprendre = false

  /**
   * Hold by the phone itself: the same call goes on, but the browser sends hold
   * music instead of the microphone and stops playing the customer's voice.
   * (Telnyx's call.hold() only pauses media: the customer would hear silence.)
   */
  readonly attenteNative = {
    mettreEnAttente: async () => {
      if (this.attente) return
      const emetteur = this.emetteurAudio()
      if (!emetteur) throw new Error('No audio sender on this call')
      const musique = musiqueDAttente()
      this.attente = { arreter: musique.arreter, micro: emetteur.track, piste: musique.piste }
      await emetteur.replaceTrack(musique.piste)
      elementAudio().muted = true
    },
    reprendre: async () => {
      const attente = this.attente
      if (!attente) return
      this.attente = null
      await this.emetteurAudio()?.replaceTrack(attente.micro)
      attente.arreter()
      elementAudio().muted = false
    },
  }

  private emetteurAudio(): RTCRtpSender | undefined {
    const pc = (this.call as unknown as { peer?: { instance?: RTCPeerConnection } }).peer?.instance
    return pc?.getSenders().find((e) => e.track?.kind === 'audio' || e.track === null)
  }

  sendDigits(chiffres: string) {
    for (const chiffre of chiffres) this.call.dtmf(chiffre)
  }

  on(evenement: EvenementAppel, rappel: (erreur?: unknown) => void) {
    if (!this.ecouteurs.has(evenement)) this.ecouteurs.set(evenement, new Set())
    this.ecouteurs.get(evenement)!.add(rappel)
  }

  removeAllListeners() {
    this.ecouteurs.clear()
  }

  /**
   * The SDK brought the call back on a new connection: a new Call object with
   * the same id. Commands now go to it; a hold in progress carries over.
   */
  reprendreSur(call: Call) {
    this.call = call
    if (this.attente) this.attenteAReprendre = true
  }

  /** Telnyx reports states; the dashboard listens for Twilio-style events. */
  miseAJour(etat: string) {
    if (this.termine) return
    if (etat === 'active' && !this.aEteActif) {
      this.aEteActif = true
      this.emettre('accept')
    } else if (etat === 'active' && this.attenteAReprendre && this.attente) {
      this.attenteAReprendre = false
      const emetteur = this.emetteurAudio()
      if (emetteur) {
        this.attente.micro = emetteur.track
        void emetteur.replaceTrack(this.attente.piste)
      }
    } else if (etat === 'hangup' || etat === 'destroy' || etat === 'purge') {
      this.termine = true
      if (this.attente) {
        this.attente.arreter()
        this.attente = null
        elementAudio().muted = false
      }
      if (this.aEteActif) this.emettre('disconnect')
      else if (this.refuse) this.emettre('reject')
      else this.emettre('cancel')
    }
  }

  get estTermine() {
    return this.termine
  }

  /** The connection is gone for good: the screen hears the call ended. */
  abandonner() {
    this.miseAJour('destroy')
  }

  private emettre(evenement: EvenementAppel, erreur?: unknown) {
    this.ecouteurs.get(evenement)?.forEach((rappel) => rappel(erreur))
  }
}

/**
 * A soft, looping hold melody made by the browser (Web Audio): no file and no
 * licence to manage. Returns the audio track to send and a stop function.
 */
function musiqueDAttente(): { piste: MediaStreamTrack; arreter: () => void } {
  const ctx = new AudioContext()
  const sortie = ctx.createMediaStreamDestination()
  const volume = ctx.createGain()
  volume.gain.value = 0.18
  volume.connect(sortie)

  // Pentatonic phrase (C major), gentle sine tones with a slow fade.
  const notes = [523.25, 659.25, 783.99, 880.0, 783.99, 659.25, 587.33, 659.25]
  const duree = 0.6
  const phrase = notes.length * duree + 1.2
  const jouerPhrase = (debut: number) => {
    notes.forEach((frequence, i) => {
      const t = debut + i * duree
      const osc = ctx.createOscillator()
      const enveloppe = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = frequence
      enveloppe.gain.setValueAtTime(0, t)
      enveloppe.gain.linearRampToValueAtTime(1, t + 0.05)
      enveloppe.gain.exponentialRampToValueAtTime(0.001, t + duree * 1.6)
      osc.connect(enveloppe).connect(volume)
      osc.start(t)
      osc.stop(t + duree * 1.7)
    })
  }
  let prochaine = ctx.currentTime + 0.1
  const planifier = () => {
    while (prochaine < ctx.currentTime + phrase * 2) {
      jouerPhrase(prochaine)
      prochaine += phrase
    }
  }
  planifier()
  const minuterie = window.setInterval(planifier, 1000)
  void ctx.resume()

  return {
    piste: sortie.stream.getAudioTracks()[0],
    arreter: () => {
      window.clearInterval(minuterie)
      void ctx.close()
    },
  }
}

function elementAudio(): HTMLAudioElement {
  const id = 'callizy-telnyx-audio'
  let element = document.getElementById(id) as HTMLAudioElement | null
  if (!element) {
    element = document.createElement('audio')
    element.id = id
    element.autoplay = true
    element.style.display = 'none'
    document.body.appendChild(element)
  }
  return element
}

function surNotification(notification: INotification) {
  if (notification.type !== 'callUpdate' || !notification.call) return
  const call = notification.call as Call & { recoveredCallId?: string }
  let appel = appels.get(call.id)
  const ancienId = call.recoveredCallId
  if (!appel && ancienId) {
    // Brought back after a dropped connection with a new id (Telnyx: "the
    // call ID may change"); recoveredCallId is the one we know.
    appel = appels.get(ancienId)
    if (appel) {
      appels.delete(ancienId)
      appels.set(call.id, appel)
    }
  }
  if (!appel) {
    // Outbound calls are registered by appelerTelnyx(); anything else unknown
    // that rings is an inbound call.
    if (call.direction !== 'inbound' || call.state !== 'ringing') return
    appel = new AppelTelnyx(call, call.options?.remoteCallerNumber || '')
    appels.set(call.id, appel)
    surEntrants.forEach((rappel) => rappel(appel!))
  }
  if (appel.call !== call) {
    // Another object for this call: the recovered one, or a late update from
    // the object it replaced (ignored).
    if (!ancienId) return
    appel.reprendreSur(call)
  }
  appel.miseAJour(call.state)
  if (appel.estTermine) {
    appels.delete(call.id)
    if (appels.size === 0 && renouvellementDemande) reconnecterSiLibre()
  }
}

const etat = (e: EtatTelephone) => changerEtatTelephone('telnyx', e)

/** The login token is a JWT: true when it has expired, or will within 5 minutes. */
function jetonPerime(jeton: string | null): boolean {
  if (!jeton) return true
  try {
    const charge = jeton.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const exp = JSON.parse(atob(charge)).exp
    return typeof exp === 'number' && exp * 1000 - Date.now() < 5 * 60_000
  } catch {
    return false
  }
}

/** Asks the dashboard for a fresh voice token; proposerJetonTelnyx() connects with it. */
function demanderJetonNeuf() {
  attenteJeton = true
  demanderUnJeton?.()
}

/** Connects the Telnyx phone. Idempotent. */
export async function connecterTelnyx(jeton: string, identite: string): Promise<void> {
  jetonDisponible = jeton
  identiteSignee = identite
  arrete = false
  surveillerLeRetour()
  if (client) return
  // An expired token would only be refused (a tab asleep for a day): get a fresh one first.
  if (jetonPerime(jeton)) {
    demanderJetonNeuf()
    return
  }
  attenteJeton = false
  const { TelnyxRTC: Client } = await import('@telnyx/webrtc')
  if (client) return
  etat(tentatives > 0 ? 'reconnexion' : 'connexion')
  // Telnyx plays no ringtone of its own: the agent hears the same one as on a
  // Twilio line (the Twilio Voice SDK default incoming sound).
  const nouveau = new Client({
    login_token: jeton,
    debug: isDev,
    ringtoneFile: SONNERIE,
    ringbackFile: TONALITE_RETOUR,
    keepConnectionAliveOnSocketClose: true,
  })
  nouveau.remoteElement = elementAudio()
  // Choix faits avant cette connexion (reconnexion du jeton comprise).
  if (microChoisi) void nouveau.setAudioSettings({ micId: microChoisi }).catch(() => undefined)
  if (hautParleurChoisi) appliquerHautParleur(nouveau, hautParleurChoisi)
  nouveau.on('telnyx.ready', () => {
    if (nouveau !== client) return
    pret = true
    tentatives = 0
    etat('pret')
  })
  nouveau.on('telnyx.socket.close', () => {
    if (nouveau !== client) return // a client we closed ourselves
    pret = false
    // Dropped (Mac asleep, Wi-Fi change): the SDK reconnects on its own and
    // brings calls back, as Telnyx recommends. Rebuilding the client here
    // would end them.
    etat('reconnexion')
  })
  nouveau.on('telnyx.error', (evenement: { error?: { code?: number } }) => {
    if (nouveau !== client) return
    const code = evenement?.error?.code
    console.error('[telnyx] error:', code ?? evenement)
    if (code !== undefined && SDK_ABANDONNE.has(code)) {
      pret = false
      planifierReconnexion()
    } else if (code !== undefined && CONNEXION_REFUSEE.has(code)) {
      pret = false
      planifierReconnexion(false, true)
    }
  })
  nouveau.on('telnyx.warning', (evenement: { warning?: { code?: number } }) => {
    // The JWT lives 24 h and this SDK version cannot swap it in place:
    // reconnect with a fresh one, between two calls. The SDK warns ~1 h ahead.
    if (evenement?.warning?.code !== TOKEN_EXPIRING_SOON) return
    renouvellementDemande = true
    if (jetonDisponible && jetonDisponible !== jetonConnecte) reconnecterSiLibre()
    else demanderUnJeton?.()
  })
  nouveau.on('telnyx.notification', surNotification)
  client = nouveau
  jetonConnecte = jeton
  nouveau.connect()
}

/** A newer token arrived (voice token refetch). Used on the next reconnect. */
export function proposerJetonTelnyx(jeton: string, identite: string) {
  jetonDisponible = jeton
  identiteSignee = identite
  if (attenteJeton && !arrete && !pret && !jetonPerime(jeton)) {
    // The fresh token we were waiting for: connect with it.
    attenteJeton = false
    fermerClient()
    void connecterTelnyx(jeton, identite).catch(() => planifierReconnexion())
    return
  }
  if (renouvellementDemande && jeton !== jetonConnecte) reconnecterSiLibre()
}

/** Called when Telnyx warns that the token is about to expire. */
export function surDemandeDeJetonTelnyx(rappel: (() => void) | null) {
  demanderUnJeton = rappel
}

function reconnecterSiLibre() {
  if (appels.size > 0 || !jetonDisponible) return
  const jeton = jetonDisponible
  renouvellementDemande = false
  const identite = identiteSignee
  fermerClient()
  if (identite) void connecterTelnyx(jeton, identite)
}

/**
 * Our own reconnection, only when the SDK cannot reconnect by itself: it gave
 * up, the login was refused, or there is no client. After 2 s, then 4, 8… up
 * to 60 s; shown as cut from the fourth attempt, while trying on. A fresh token
 * is fetched when the login was refused, the token is expired, or from the
 * second attempt.
 */
function planifierReconnexion(immediat = false, jetonNeuf = false) {
  if (arrete || !identiteSignee) return
  if (minuterieReconnexion) {
    if (!immediat) return
    clearTimeout(minuterieReconnexion)
  }
  const delai = immediat ? 0 : Math.min(2000 * 2 ** tentatives, 60_000)
  minuterieReconnexion = setTimeout(() => {
    minuterieReconnexion = null
    if (arrete || pret) return
    tentatives += 1
    etat(tentatives > 3 ? 'coupe' : 'reconnexion')
    // Calls on that connection are over: the screen hears it.
    fermerClient()
    const jeton = jetonDisponible
    const identite = identiteSignee
    if (jetonNeuf || tentatives >= 2 || jetonPerime(jeton) || !identite) {
      demanderJetonNeuf()
      return
    }
    void connecterTelnyx(jeton!, identite).catch(() => planifierReconnexion())
  }, delai)
}

/**
 * Brings the phone back now: Reconnect button, a call placed while it is not
 * ready, the tab visible again, the health check. While the SDK reconnects,
 * this only shortens its wait (ignored if its connection is alive); otherwise
 * it starts over.
 */
export function relancerTelnyx() {
  if (arrete || pret || !identiteSignee) return
  if (client && !minuterieReconnexion && !attenteJeton) {
    void client.connect()
    return
  }
  tentatives = 0
  planifierReconnexion(true, attenteJeton)
}

/** Calls on a connection we drop: the screen hears they ended. */
function abandonnerAppels() {
  appels.forEach((appel) => appel.abandonner())
  appels.clear()
}

let surveillanceInstallee = false

/** Back online, or the tab visible again (Mac reopened): reconnect right away. */
function surveillerLeRetour() {
  if (surveillanceInstallee || typeof window === 'undefined') return
  surveillanceInstallee = true
  const auRetour = () => relancerTelnyx()
  window.addEventListener('online', auRetour)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') auRetour()
  })
}

function appliquerHautParleur(cible: TelnyxRTC, deviceId: string) {
  cible.speaker = deviceId
  const element = elementAudio() as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }
  void element.setSinkId?.(deviceId).catch(() => undefined)
}

/** Microphone picked in the audio popover; kept for the next connections. */
export async function choisirMicroTelnyx(deviceId: string) {
  microChoisi = deviceId
  if (client) await client.setAudioSettings({ micId: deviceId })
}

/** Speaker picked in the audio popover; kept for the next connections. */
export function choisirHautParleurTelnyx(deviceId: string) {
  hautParleurChoisi = deviceId
  if (client) appliquerHautParleur(client, deviceId)
}

/** Logout / leaving the dashboard: disconnect and stop reconnecting. */
export function deconnecterTelnyx() {
  arrete = true
  tentatives = 0
  attenteJeton = false
  if (minuterieReconnexion) clearTimeout(minuterieReconnexion)
  minuterieReconnexion = null
  fermerClient()
  etat('eteint')
}

function fermerClient() {
  if (!client) return
  try {
    // Without clearReconnectToken the SDK may log back in on its own (Telnyx).
    // Its listeners go with it: the next connection builds a new client.
    client.clearReconnectToken()
    void client.disconnect()
  } catch (erreur) {
    if (isDev) console.warn('[telnyx] disconnect:', erreur)
  }
  client = null
  pret = false
  jetonConnecte = null
  abandonnerAppels()
}

export function telnyxPret(): boolean {
  return pret
}

export function telnyxActif(): boolean {
  return client !== null
}

/** Places a call; the TeXML webhook checks the caller ID server-side. */
export function appelerTelnyx(vers: string, numeroAffiche?: string): AppelAgent {
  if (!client || !pret || !identiteSignee) throw new Error('Telnyx phone not connected')
  const call = client.newCall({
    destinationNumber: vers,
    callerNumber: numeroAffiche,
    audio: true,
    customHeaders: [{ name: 'X-Callizy-Agent', value: identiteSignee }],
  })
  const appel = new AppelTelnyx(call, vers)
  appels.set(call.id, appel)
  return appel
}

/** Subscribes to inbound calls. Returns the unsubscribe function. */
export function surAppelEntrantTelnyx(rappel: (appel: AppelAgent) => void): () => void {
  surEntrants.add(rappel)
  return () => surEntrants.delete(rappel)
}
